import { useState } from "react";
import { Contract, parseUnits, formatUnits, isAddress } from "ethers";
import { Loader2 } from "lucide-react";
import TransactionModal from "../../../../modals/TransactionModal";
import { useSmartWalletSigner } from "../../../../hooks/useSmartWalletSigner";
import {
  ADDRESSES,
  erc20Abi,
  stakingRewardsAbi,
  VALID_SYMBOLS,
  TokenSymbol,
} from "../../../../config/contracts";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface TabStakeProps {
  balances: Record<TokenSymbol, number>;
  setBalances: (b: Record<TokenSymbol, number>) => void;
  fetchDashboard?: () => Promise<void>;
  setBalanceRefresh: React.Dispatch<React.SetStateAction<number>>;
}

const TabStake: React.FC<TabStakeProps> = ({
  balances,
  setBalances,
  fetchDashboard,
  setBalanceRefresh,
}) => {
  const {
    signer,
    isConnected,
    loading: signerLoading,
  } = useSmartWalletSigner();

  const [selectedToken, setSelectedToken] = useState<TokenSymbol>("NAU");
  const [amount, setAmount] = useState("");
  const [txHash, setTxHash] = useState<string | null>(null);
  const [txStatus, setTxStatus] = useState<
    "pending" | "confirmed" | "failed" | null
  >(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleStake = async () => {
    setTxHash(null);
    setTxStatus(null);

    if (signerLoading) {
      toast.info("Connecting to wallet...");
      return;
    }

    if (!isConnected || !signer) {
      toast.error("Wallet not connected.");
      return;
    }

    setIsProcessing(true);
    setTxStatus("pending");

    try {
      const userAddress = await signer.getAddress();
      const tokenAddr = ADDRESSES[selectedToken];
      const stakingAddr =
        ADDRESSES[`${selectedToken}_STAKING` as keyof typeof ADDRESSES];

      if (!tokenAddr || !isAddress(tokenAddr))
        throw new Error("Invalid token address.");
      if (!stakingAddr || !isAddress(stakingAddr))
        throw new Error("Invalid staking contract.");

      const tokenContract = new Contract(tokenAddr, erc20Abi, signer);
      const stakingContract = new Contract(
        stakingAddr,
        stakingRewardsAbi,
        signer
      );

      const amountBN = parseUnits(amount);
      if (amountBN <= 0n)
        throw new Error("Stake amount must be greater than 0.");

      // Approve
      toast.info("Approving tokens...");
      const approveTx = await tokenContract.approve(stakingAddr, amountBN);
      await approveTx.wait();

      // Stake
      toast.info("Staking tokens...");
      const stakeTx = await stakingContract.stake(amountBN);
      setTxHash(stakeTx.hash);
      await stakeTx.wait();

      setTxStatus("confirmed");
      toast.success("✅ Tokens successfully staked.");
      setAmount("");

      // Update balances
      const updatedStakedRaw = await stakingContract.balanceOf(userAddress);
      const updatedStaked = parseFloat(formatUnits(updatedStakedRaw));
      setBalances({ ...balances, [selectedToken]: updatedStaked });

      setBalanceRefresh((prev) => prev + 1);
      if (fetchDashboard) await fetchDashboard();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message.includes("reverted")
            ? "Transaction reverted. Please check your token approval and try again."
            : err.message
          : "Staking failed.";
      toast.error(message);
      setTxStatus("failed");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-4 space-y-5 text-red-900 font-outfit">
      {txStatus && (
        <TransactionModal
          txStatus={txStatus}
          txHash={txHash}
          userAddress={signer?.address || null}
          closeModal={() => {
            setTxStatus(null);
            setTxHash(null);
          }}
        />
      )}

      <h2 className="text-l text-center text-red-900">
        Choose the token you want to stake
      </h2>

      {/* Token Selector */}
      <div className="flex flex-wrap justify-center gap-3">
        {VALID_SYMBOLS.map((sym) => (
          <button
            key={sym}
            onClick={() => {
              setSelectedToken(sym);
              setAmount("");
            }}
            className={`px-4 py-1.5 text-sm font-semibold transition ${
              selectedToken === sym
                ? "bg-white text-red-600 shadow"
                : "bg-white/40 text-red-900 hover:bg-white/30"
            }`}
          >
            {sym}
          </button>
        ))}
      </div>

      {/* Input Field */}
      <input
        type="text"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder="Enter amount to stake"
        className="w-full px-4 py-2 text-red-900 placeholder-red-400 bg-white/60 border border-white/30 focus:outline-none focus:ring-2 focus:ring-red-500"
      />

      {/* Stake Button */}
      <button
        onClick={handleStake}
        disabled={
          isProcessing ||
          signerLoading ||
          !amount ||
          isNaN(Number(amount)) ||
          Number(amount) <= 0
        }
        className="w-full py-2  bg-red-600 hover:bg-red-500 active:bg-red-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition"
      >
        {(isProcessing || signerLoading) && (
          <Loader2 className="w-4 h-4 animate-spin" />
        )}
        {isProcessing || signerLoading
          ? "Processing..."
          : `Stake ${selectedToken}`}
      </button>
    </div>
  );
};

export default TabStake;
